import axios from "axios";

const BASE_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://127.0.0.1:8024/api/v1";

/**
 * Fetch master document requirements catalog
 * GET /api/v1/document-requirements
 */
export const getMasterDocumentRequirements = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/document-requirements`);
    let hiringTypes = [];

    if (res.data?.hiring_types) {
      hiringTypes = res.data.hiring_types;
    } else if (res.data?.data?.hiring_types) {
      hiringTypes = res.data.data.hiring_types;
    } else if (Array.isArray(res.data)) {
      hiringTypes = res.data;
    }

    return hiringTypes;
  } catch (error) {
    console.error("Error fetching master document requirements:", error);
    throw error;
  }
};

/**
 * Fetch existing customer document requirements list
 * GET /api/v1/customer-document-requirements
 */
export const getCustomerDocumentRequirements = async (orgId, customerId, page = 1, maxRows = 100) => {
  try {
    const res = await axios.get(`${BASE_URL}/customer-document-requirements`, {
      params: {
        org_id: orgId,
        customer_id: customerId,
        page_no: page,
        max_rows: maxRows,
      },
    });
    return res.data;
  } catch (error) {
    console.error("Error fetching customer document requirements:", error);
    throw error;
  }
};

/**
 * Fetch full document configuration (including verification mode, field suggestions, and saved selections)
 * GET /api/v1/customer-document-requirements/config
 */
export const getDocumentConfiguration = async (orgId, customerId) => {
  try {
    const res = await axios.get(`${BASE_URL}/customer-document-requirements/config`, {
      params: {
        org_id: orgId,
        customer_id: customerId,
      },
    });
    return res.data;
  } catch (error) {
    // If backend endpoint is not yet deployed on remote server (404), return null to trigger fallback
    if (error.response && error.response.status === 404) {
      console.warn("GET /customer-document-requirements/config endpoint not yet available on server, using fallback");
      return null;
    }
    console.error("Error fetching document configuration:", error);
    throw error;
  }
};

/**
 * Create new customer document requirement mapping
 * POST /api/v1/customer-document-requirements
 */
export const createCustomerDocumentRequirement = async (
  orgIdOrOptions,
  customerId,
  hiringType,
  documentRequirementId,
  isMandatory = true
) => {
  try {
    let payload;

    // Support both object argument and legacy positional arguments
    if (typeof orgIdOrOptions === "object" && orgIdOrOptions !== null) {
      const opts = orgIdOrOptions;
      const orgId = opts.orgId;
      const custId = opts.customerId;
      const hType = opts.hiringType;
      const isCustom = opts.isCustom;
      const mandatory = opts.isMandatory !== undefined ? opts.isMandatory : true;

      if (isCustom) {
        payload = {
          org_id: orgId,
          customer_id: custId,
          hiring_types: [
            {
              hiring_type: hType,
              documents: [],
              others: [
                {
                  document_type: (opts.documentType || "DOCUMENT").toUpperCase().replace(/\s+/g, "_"),
                  display_name: opts.displayName || opts.documentType,
                  is_mandatory: mandatory,
                },
              ],
            },
          ],
        };
      } else {
        payload = {
          org_id: orgId,
          customer_id: custId,
          hiring_types: [
            {
              hiring_type: hType,
              documents: [
                {
                  document_requirement_id: opts.documentRequirementId,
                  is_mandatory: mandatory,
                },
              ],
              others: [],
            },
          ],
        };
      }
    } else {
      payload = {
        org_id: orgIdOrOptions,
        customer_id: customerId,
        hiring_types: [
          {
            hiring_type: hiringType,
            documents: [
              {
                document_requirement_id: documentRequirementId,
                is_mandatory: isMandatory,
              },
            ],
            others: [],
          },
        ],
      };
    }

    const res = await axios.post(`${BASE_URL}/customer-document-requirements`, payload);
    return res.data;
  } catch (error) {
    console.error("Error creating customer document requirement:", error);
    throw error;
  }
};

/**
 * Save verification mode (MANUAL or AI) for a mapping
 * POST /api/v1/customer-document-requirements/{mapping_id}/verification-mode
 */
export const saveVerificationMode = async (mappingId, orgId, customerId, verificationMode) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/customer-document-requirements/${mappingId}/verification-mode`,
      { verification_mode: verificationMode },
      {
        params: {
          org_id: orgId,
          customer_id: customerId,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error saving verification mode for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * Save field selections for a mapping
 * POST /api/v1/customer-document-requirements/{mapping_id}/field-selections
 */
export const saveFieldSelections = async (mappingId, orgId, customerId, fields) => {
  try {
    const payload = {
      fields: fields.map((f) => ({
        suggested_field_id: f.suggested_field_id || null,
        field_key: f.field_key.trim(),
        display_name: f.display_name.trim(),
        description: f.description || null,
        data_type: f.data_type || "STRING",
        is_required: Boolean(f.is_required),
        source: f.suggested_field_id ? "suggestion" : "custom",
      })),
    };

    const res = await axios.post(
      `${BASE_URL}/customer-document-requirements/${mappingId}/field-selections`,
      payload,
      {
        params: {
          org_id: orgId,
          customer_id: customerId,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error saving field selections for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * Fetch extraction prompt for a document configuration
 * GET /api/v1/customer-document-requirements/{mapping_id}/extraction-prompt
 */
export const getExtractionPrompt = async (mappingId, orgId, customerId) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/customer-document-requirements/${mappingId}/extraction-prompt`,
      {
        params: { org_id: orgId, customer_id: customerId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error fetching extraction prompt for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * Update extraction prompt for a document configuration
 * PUT /api/v1/customer-document-requirements/{mapping_id}/extraction-prompt
 */
export const updateExtractionPrompt = async (mappingId, orgId, customerId, promptText, changeSummary = null) => {
  try {
    const res = await axios.put(
      `${BASE_URL}/customer-document-requirements/${mappingId}/extraction-prompt`,
      {
        prompt_instructions: promptText,
        prompt_text: promptText,
        change_summary: changeSummary,
      },
      {
        params: { org_id: orgId, customer_id: customerId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error updating extraction prompt for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * AI Copilot: Generate/refine rule directive from natural language
 * POST /api/v1/customer-document-requirements/{mapping_id}/extraction-prompt/ai-assist
 */
export const aiAssistExtractionPrompt = async (mappingId, orgId, customerId, instruction, currentPrompt = "") => {
  try {
    const res = await axios.post(
      `${BASE_URL}/customer-document-requirements/${mappingId}/extraction-prompt/ai-assist`,
      {
        instruction,
        current_prompt: currentPrompt,
      },
      {
        params: { org_id: orgId, customer_id: customerId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error in AI assist for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * Get version history for extraction prompt
 * GET /api/v1/customer-document-requirements/{mapping_id}/extraction-prompt/versions
 */
export const getExtractionPromptVersions = async (mappingId, orgId, customerId) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/customer-document-requirements/${mappingId}/extraction-prompt/versions`,
      {
        params: { org_id: orgId, customer_id: customerId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error fetching prompt versions for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * Rollback extraction prompt to a historical version
 * POST /api/v1/customer-document-requirements/{mapping_id}/extraction-prompt/rollback/{version}
 */
export const rollbackExtractionPromptVersion = async (mappingId, version, orgId, customerId) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/customer-document-requirements/${mappingId}/extraction-prompt/rollback/${version}`,
      {},
      {
        params: { org_id: orgId, customer_id: customerId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error rolling back prompt for mapping ${mappingId} to v${version}:`, error);
    throw error;
  }
};


/**
 * Generate AI extraction prompt from selected fields
 * POST /api/v1/customer-document-requirements/{mapping_id}/extraction-prompt/generate
 */
export const generateExtractionPrompt = async (mappingId, orgId, customerId, confirmRegenerate = true) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/customer-document-requirements/${mappingId}/extraction-prompt/generate`,
      { confirm_regenerate: confirmRegenerate },
      {
        params: { org_id: orgId, customer_id: customerId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error generating extraction prompt for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * Regenerate AI extraction prompt from selected fields
 * POST /api/v1/customer-document-requirements/{mapping_id}/extraction-prompt/regenerate
 */
export const regenerateExtractionPrompt = async (mappingId, orgId, customerId) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/customer-document-requirements/${mappingId}/extraction-prompt/regenerate`,
      {},
      {
        params: { org_id: orgId, customer_id: customerId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error regenerating extraction prompt for mapping ${mappingId}:`, error);
    throw error;
  }
};

/**
 * Sync candidate replies from Nylas for attachments
 * POST /api/v1/pre-onboardings/sync-replies or /api/v1/pre-onboardings/{id}/sync-replies
 */
export const syncCandidateReplies = async (preOnboardingId = null) => {
  try {
    const url = preOnboardingId
      ? `${BASE_URL}/pre-onboardings/${preOnboardingId}/sync-replies`
      : `${BASE_URL}/pre-onboardings/sync-replies`;
    const res = await axios.post(url);
    return res.data;
  } catch (error) {
    console.error("Error syncing candidate replies:", error);
    throw error;
  }
};

/**
 * Manual document upload on behalf of candidate
 * POST /api/v1/pre-onboardings/{pre_onboarding_id}/documents
 */
export const uploadManualDocument = async (preOnboardingId, docType, file) => {
  try {
    const formData = new FormData();
    formData.append("doc_types", docType);
    formData.append("files", file);

    const res = await axios.post(
      `${BASE_URL}/pre-onboardings/${preOnboardingId}/documents`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error manually uploading document for onboarding ${preOnboardingId}:`, error);
    throw error;
  }
};

/**
 * Batch manual verification review (Approve / Reject)
 * PATCH /api/v1/pre-onboardings/{pre_onboarding_id}/documents/verification
 */
export const batchManualVerify = async (preOnboardingId, verifiedBy, documents) => {
  try {
    const numericVerifiedBy = parseInt(verifiedBy, 10) || 1;
    const res = await axios.patch(
      `${BASE_URL}/pre-onboardings/${preOnboardingId}/documents/verification`,
      {
        verified_by: numericVerifiedBy,
        documents: documents.map((d) => ({
          document_id: parseInt(d.document_id, 10),
          is_verified: d.is_verified !== undefined ? Boolean(d.is_verified) : (d.verification_status === "VERIFIED"),
          verification_reason: d.verification_reason || "Manually reviewed by recruiter",
        })),
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error submitting manual verification for onboarding ${preOnboardingId}:`, error);
    throw error;
  }
};

/**
 * Fetch candidates in pre-onboarding workflow
 * GET /api/v1/pre-onboardings
 */
export const getPreOnboardings = async ({ orgId, customerId, page = 1, maxRows = 20 } = {}) => {
  try {
    const params = {
      page_no: page,
      max_rows: maxRows,
    };
    if (orgId) params.org_id = orgId;
    if (customerId) params.customer_id = customerId;

    const res = await axios.get(`${BASE_URL}/pre-onboardings`, { params });
    return res.data;
  } catch (error) {
    console.error("Error fetching pre-onboardings:", error);
    throw error;
  }
};

/**
 * Delete / deactivate a customer document requirement
 * DELETE /api/v1/customer-document-requirements/{mapping_id}
 */
export const deleteCustomerDocumentRequirement = async (mappingId, orgId, customerId) => {
  try {
    const res = await axios.delete(
      `${BASE_URL}/customer-document-requirements/${mappingId}`,
      {
        params: {
          org_id: orgId,
          customer_id: customerId,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.error(`Error deleting customer document requirement ${mappingId}:`, error);
    throw error;
  }
};

